import { createAdminTerms, getAdminTermsCategories, queryKeys } from "@dutyon/apis";
import {
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Paper,
  RichEditor,
  SafeHtml,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function TermsCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [categoryKey, setCategoryKey] = useState<string>("");
  const [version, setVersion] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [isRequired, setIsRequired] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isHtmlMode, setIsHtmlMode] = useState<boolean>(false);

  // 카테고리 목록 조회
  const { data: categories } = useQuery({
    queryKey: queryKeys.terms.categories(),
    queryFn: getAdminTermsCategories,
  });

  const createMutation = useMutation({
    mutationFn: createAdminTerms,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
      navigate("/terms");
    },
  });

  const handleSubmit = () => {
    if (!categoryKey) {
      alert("약관 구분을 선택해주세요.");
      return;
    }
    if (!version.trim()) {
      alert("약관 버전을 입력해주세요.");
      return;
    }
    if (!title.trim()) {
      alert("약관 제목을 입력해주세요.");
      return;
    }
    if (!content.trim()) {
      alert("약관 내용을 입력해주세요.");
      return;
    }

    if (isActive) {
      const confirmMsg =
        "새로운 약관을 즉시 활성화(발행)하시겠습니까?\n발행 즉시 동일한 약관 구분의 기존 활성 약관이 비활성화되며, 모든 사용자는 다음 로그인 시 재동의가 요구됩니다.";
      if (!confirm(confirmMsg)) {
        return;
      }
    }

    createMutation.mutate({
      categoryKey,
      version,
      title,
      content,
      isRequired,
      isActive,
    });
  };

  const selectData =
    categories?.map((cat) => ({
      value: cat.key,
      label: `${cat.name} (${cat.key})`,
    })) || [];

  return (
    <TitleContainer
      title="새 약관 발행"
      rightSection={
        <Button
          variant="subtle"
          color="gray"
          leftSection={<ArrowLeft size={16} />}
          onClick={() => navigate("/terms")}
        >
          목록으로 돌아가기
        </Button>
      }
    >
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card withBorder radius="lg" shadow="sm" p="lg">
            <Stack gap="md">
              <Text fw={700} size="lg">
                약관 정보 입력
              </Text>

              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select
                    label="약관 구분 (Category)"
                    placeholder="약관 구분을 선택하세요"
                    data={selectData}
                    value={categoryKey}
                    onChange={(val) => setCategoryKey(val || "")}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="약관 버전 (Version)"
                    placeholder="예: 1.0, 1.1, 2026-07-26 등"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    required
                  />
                </Grid.Col>
              </Grid>

              <TextInput
                label="약관 제목"
                placeholder="사용자에게 보여질 약관 제목을 입력하세요."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />

              <Group justify="space-between" mt="xs">
                <Group gap="lg">
                  <Switch
                    label="필수 동의 여부"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.currentTarget.checked)}
                  />
                  <Switch
                    label="즉시 활성화 (발행)"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.currentTarget.checked)}
                  />
                </Group>
                <Switch
                  label="HTML 직접 타이핑 모드"
                  checked={isHtmlMode}
                  onChange={(e) => setIsHtmlMode(e.currentTarget.checked)}
                />
              </Group>

              <Divider label="약관 내용" labelPosition="center" my="sm" />

              <Stack gap="xs">
                {isHtmlMode ? (
                  <Textarea
                    label="HTML 소스 직접 편집"
                    placeholder="HTML 코드를 직접 작성하세요."
                    minRows={14}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    styles={{ input: { fontFamily: "monospace", fontSize: "13px" } }}
                  />
                ) : (
                  <RichEditor
                    label="WYSIWYG 에디터 편집"
                    value={content}
                    onChange={(html) => setContent(html)}
                    placeholder="약관 전문 내용을 작성하세요. HTML 태그가 지원됩니다."
                    minHeight={300}
                  />
                )}
              </Stack>

              <Group justify="flex-end" mt="lg">
                <Button variant="light" color="gray" onClick={() => navigate("/terms")}>
                  취소
                </Button>
                <Button
                  leftSection={<Check size={16} />}
                  onClick={handleSubmit}
                  loading={createMutation.isPending}
                >
                  발행 및 등록
                </Button>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="md" style={{ position: "sticky", top: 20 }}>
            <Card withBorder radius="lg" shadow="sm" p="lg" bg="gray.0">
              <Stack gap="xs">
                <Text fw={700} size="md">
                  실시간 렌더링 프리뷰
                </Text>
                <Text size="xs" c="dimmed">
                  사용자 화면에 노출될 최종 HTML 모습입니다.
                </Text>
                <Divider my="xs" />
                <Paper
                  withBorder
                  p="md"
                  radius="md"
                  bg="white"
                  style={{
                    minHeight: 450,
                    maxHeight: 600,
                    overflowY: "auto",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  {title && (
                    <Text fw={700} size="sm" mb="sm" c="blue.7">
                      [{isRequired ? "필수" : "선택"}] {title}
                    </Text>
                  )}
                  {content ? (
                    <SafeHtml html={content} />
                  ) : (
                    <Text size="xs" c="dimmed" ta="center" mt="xl">
                      작성 중인 내용이 여기에 실시간으로 렌더링됩니다.
                    </Text>
                  )}
                </Paper>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </TitleContainer>
  );
}
